import { ButtonStyle } from './../../../system/modal-confirm/modal-confirm.component';
import { ToastrService } from 'ngx-toastr';
import { FilterBuilderFilter } from './../../../../models/filter-builder';
import { WorkflowType } from './../../../../models/workflow';
import {
  FilterBuilderParam,
  FilterBuilderOutput
} from 'src/app/models/filter-builder';
import {
  PagedDailyFeeReportResponse,
  DailyFeeReportRequest,
  FeeTransaction,
  PagedDailyFeeReportRequest
} from './../../../../models/financial-reports';
import {
  Component,
  OnInit,
  Output,
  EventEmitter,
  ViewChild,
  TemplateRef
} from '@angular/core';
import {
  SystemService,
  WorkflowContextService,
  DataService,
  SecurityService
} from 'src/app/services';
import { ApplicationStatusPipe } from 'src/app/pipes/application-status.pipe';
import { Actions } from 'src/app/models';
import { Utilities } from 'src/app/services';
import { TableColumn } from '@swimlane/ngx-datatable';
import { DataTableHelpers } from 'src/app/components/system/datatable/datatable-helper';
import { ColumnOption, GridSettings } from 'src/app/models/grid-settings';
import { ComponentIds } from 'src/app/models/component-ids';

@Component({
  selector: 'wm-daily-fees',
  templateUrl: './daily-fees.component.html',
  styleUrls: ['./daily-fees.component.css'],
  standalone: false
})
export class DailyFeesComponent implements OnInit {
  loading = true;
  id = ComponentIds.FEES;
  title = 'Fees';
  report: PagedDailyFeeReportResponse;
  defaultFilters: FilterBuilderFilter[];
  timeFrame: FilterBuilderParam = {
    name: 'Paid Date',
    id: 'timeframe',
    inputType: 'date',
    types: ['range']
  };
  params: FilterBuilderParam[] = [
    {
      name: 'Permit Number',
      types: ['is', 'contains'],
      id: 'permitNumber'
    },
    {
      name: 'Amount',
      id: 'amount',
      types: ['is'],
      inputType: 'number'
    },
    this.timeFrame,
    {
      name: 'Recipient',
      id: 'recipientName'
    },
    {
      name: 'Application',
      id: 'applicationName',
      options: []
    },
    {
      name: 'Payer',
      id: 'payer'
    },
    {
      name: 'Status',
      id: 'status',
      types: ['is', 'in'],
      options: [
        {
          name: 'Not Started',
          value: '0'
        },
        {
          name: 'In Progress',
          value: '1'
        },
        {
          name: 'Completed',
          value: '2'
        },
        {
          name: 'Voided',
          value: '3'
        },
        {
          name: 'Rejected',
          value: '4'
        },
        {
          name: 'Discarded',
          value: '5'
        }
      ]
    }
  ];

  filters: FilterBuilderOutput = {};

  public useExcelFormat: boolean;

  @ViewChild('permitNumTmpl', { static: true }) permitNumTmpl: TemplateRef<any>;
  @ViewChild('paymentDateTmpl', { static: true }) paymentDateTmpl: TemplateRef<
    any
  >;

  ButtonStyle = ButtonStyle;
  columns: TableColumn[];
  availableColumns: TableColumn[];
  columnOptions: ColumnOption[] = [];
  exportColumns: string[];
  rows: any[];
  public page = {
    // The number of elements in the page
    size: 10,
    // The total number of elements
    totalElements: 0,
    // The total number of pages
    totalPages: 0,
    // The current page number
    pageNumber: 0
  };
  public sortField: string;
  public sortDescending = false;

  lastQuery = {};

  // column prop strings to be hidden on the grid by default
  defaultHiddenColumns: string[] = ['appDescription', 'clientName'];
  // column prop strings that should have no option to hide in the grid, and are not exportable (such as Actions button)
  actionColumns: string[] = [];
  // column prop strings to be excluded from exports by default
  defaultExportExludedColumns: string[] = [];

  constructor(
    private systemSvc: SystemService,
    private dataSvc: DataService,
    private securitySvc: SecurityService,
    private toast: ToastrService,
    private _context: WorkflowContextService,
    private appStatusPipe: ApplicationStatusPipe,
    private _dtHelpers: DataTableHelpers
  ) {}

  ngOnInit() {
    this.defaultFilters = [
      new FilterBuilderFilter({
        type: 'range',
        value: 'this_1_months',
        queryType: 'range',
        param: this.timeFrame,
        paramId: this.timeFrame.id
      })
    ];

    this.getApplications();

    this.columns = [
      {
        prop: 'permitId',
        name: 'Permit #',
        cellTemplate: this.permitNumTmpl
      },
      {
        prop: 'amount',
        name: 'Amount'
      },
      {
        prop: 'paymentDate',
        name: 'Paid',
        cellTemplate: this.paymentDateTmpl
      },
      {
        prop: 'recipientName',
        name: 'Recipient'
      },
      {
        prop: 'applicationName',
        name: 'Application'
      },
      {
        prop: 'appStatus',
        name: 'Status'
      },
      {
        prop: 'appDescription',
        name: 'Application Description'
      },
      {
        prop: 'clientName',
        name: 'Jurisdiction'
      }
    ];
    this.setAvailableColumns();
  }

  setAvailableColumns() {
    this.availableColumns = this.columns;
  }

  isEquivalent(a: any, b: any) {
    const aProps = Object.getOwnPropertyNames(a);
    const bProps = Object.getOwnPropertyNames(b);

    if (aProps.length !== bProps.length) {
      return false;
    }

    for (let i = 0; i < aProps.length; i++) {
      const propName = aProps[i];
      if (a[propName] !== b[propName]) {
        return false;
      }
    }
    return true;
  }

  resetColumnWidths() {
    for (const c of this.columns) {
      c.width = null;
    }

    this.columns = [...this.columns];
  }

  handleSettingsChanged(event: GridSettings) {
    this.filters = event.filters;
    this.page.size = event.pageSize;
    this.page.pageNumber = event.pageNumber;
    this.sortField = event.sortField;
    this.sortDescending = event.sortDescending;
    this.columnOptions = event.columnOptions;

    const visibleColumnProps = this.columnOptions
      .filter(co => co.checked)
      .map(co => co.name);

    this.columns = this.availableColumns.filter(
      c =>
        visibleColumnProps.includes(c.prop as string) ||
        this.actionColumns.includes(c.prop as string)
    );

    this.exportColumns = this.columnOptions
      .filter(co => co.includeInExport)
      .map(co => co.name);

    this.getReport();
  }

  handleColumnOptionsChanged(event: ColumnOption[]) {
    this.columnOptions = event;
    this.columnOptions = [...this.columnOptions];

    this.exportColumns = this.columnOptions
      .filter(co => co.includeInExport)
      .map(co => co.name);
  }

  getApplications() {
    this.dataSvc
      .getPublishedWorkflows(this._context.client, [
        WorkflowType.ContractorRegistration,
        WorkflowType.ContractorRenewal,
        WorkflowType.Other,
        WorkflowType.Permit,
        WorkflowType.Renewable,
        WorkflowType.MobileInspection
      ])
      .subscribe(workflows => {
        const param = this.params.find(p => p.id === 'applicationName');
        param.options = workflows
          .map(w => ({
            name: w.version.name,
            value: w.id
          }))
          .sort((a: any, b: any) => a.name - b.name);
      });
  }

  private getQuery(): DailyFeeReportRequest {
    const filters = [];
    for (const id in this.filters) {
      if (this.filters[id]) {
        filters.push(this.filters[id]);
      }
    }

    const query = {
      clientId: this._context.client.id,
      filters,
      useExcelFormat: this.useExcelFormat,
      sortField: this.sortField,
      sortDescending: this.sortDescending,
      exportColumns: this.exportColumns
    };

    return query;
  }

  private getPagedQuery(): PagedDailyFeeReportRequest {
    const filters = [];
    for (const id in this.filters) {
      if (this.filters[id]) {
        filters.push(this.filters[id]);
      }
    }

    const query = {
      clientId: this._context.client.id,
      filters,
      pageNumber: this.page.pageNumber,
      pageSize: this.page.size,
      sortField: this.sortField,
      sortDescending: this.sortDescending
    };

    return query;
  }

  getReport() {
    const query = this.getPagedQuery();
    if (!this.isEquivalent(query, this.lastQuery)) {
      this.lastQuery = { ...query };
      this.loading = true;
      this.systemSvc.getPagedDailyFeeReport(query).subscribe(report => {
        this.page.totalElements = report.recordCount;
        this.page.totalPages = report.pageCount;
        this.rows = report.items.map(i => {
          i['permitId'] = i.permitId;
          i['amount'] = Utilities.formatCurrency(i.amount);
          i['paymentDate'] = i.paymentDate;
          i['recipientName'] = i.recipientName;
          i['applicationName'] = i.applicationName;
          i['username'] = i.username;
          i['appStatus'] = this.appStatusPipe.transform(i.appStatus);
          i['appDescription'] = i.applicationDescription;
          i['clientName'] = i.clientName;
          return i;
        });
        this.report = report;
        this.loading = false;
      });
    }
  }

  exportExcel() {
    this.useExcelFormat = true;
    this.export();
  }

  exportCsv() {
    this.useExcelFormat = false;
    this.export();
  }

  export() {
    const query = this.getQuery();
    this.systemSvc.exportDailyFeeReport(query).subscribe(
      data => {
        if (this.useExcelFormat) {
          const blob = new Blob([data], {
            type:
              'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
          });
          const link = document.createElement('a');
          link.href = window.URL.createObjectURL(blob);
          link.download = `fee-report-export-${new Date().getTime()}.xlsx`;
          link.click();
        } else {
          const blob = new Blob([data], { type: 'text/csv' });
          const link = document.createElement('a');
          link.href = window.URL.createObjectURL(blob);
          link.download = `fee-report-export-${new Date().getTime()}.csv`;
          link.click();
        }
      },
      err => {
        this.toast.error('Problem while downloading the file.');
        console.error(err);
      }
    );
  }
}
